import React, { useEffect, useState, Component } from 'react';
import { Plus, Edit2, Trash2, User, Shield, X, Eye, EyeOff } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { useToast } from '../components/ui/Toast';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
interface AdminUser {
  id: string;
  username: string;
  password: string;
  name: string;
  email: string;
  role: 'admin' | 'supervisor';
  permissions: {
    canManageOrders: boolean;
    canManageMenu: boolean;
    canManageEmployees: boolean;
    canManageCompanies: boolean;
    canViewReports: boolean;
    canManageUsers: boolean;
  };
  status: 'Active' | 'Inactive';
  createdAt: string;
}
export function UserManagement() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<AdminUser | null>(null);
  const {
    showToast
  } = useToast();
  // Load users from localStorage (in production, this would be Supabase)
  useEffect(() => {
    loadUsers();
  }, []);
  const loadUsers = () => {
    const saved = localStorage.getItem('criket_admin_users');
    if (saved) {
      setUsers(JSON.parse(saved));
    } else {
      // Default admin user
      const defaultUsers: AdminUser[] = [{
        id: 'admin-1',
        username: 'admin',
        password: 'admin123',
        name: 'Administrador Principal',
        email: 'admin@lacochera.com',
        role: 'admin',
        permissions: {
          canManageOrders: true,
          canManageMenu: true,
          canManageEmployees: true,
          canManageCompanies: true,
          canViewReports: true,
          canManageUsers: true
        },
        status: 'Active',
        createdAt: new Date().toISOString()
      }];
      setUsers(defaultUsers);
      localStorage.setItem('criket_admin_users', JSON.stringify(defaultUsers));
    }
  };
  const saveUsers = (updatedUsers: AdminUser[]) => {
    setUsers(updatedUsers);
    localStorage.setItem('criket_admin_users', JSON.stringify(updatedUsers));
  };
  const handleAddUser = () => {
    setEditingUser(null);
    setShowModal(true);
  };
  const handleEdit = (user: AdminUser) => {
    setEditingUser(user);
    setShowModal(true);
  };
  const handleDelete = (user: AdminUser) => {
    if (user.username === 'admin') {
      showToast('error', 'No permitido', 'No puedes eliminar el usuario administrador principal');
      return;
    }
    setDeleteConfirm(user);
  };
  const confirmDelete = () => {
    if (deleteConfirm) {
      const updatedUsers = users.filter(u => u.id !== deleteConfirm.id);
      saveUsers(updatedUsers);
      showToast('success', 'Usuario eliminado', `${deleteConfirm.name} ha sido eliminado`);
      setDeleteConfirm(null);
    }
  };
  const handleSave = (userData: Omit<AdminUser, 'id' | 'createdAt'>) => {
    if (editingUser) {
      // Update existing user
      const updatedUsers = users.map(u => u.id === editingUser.id ? {
        ...u,
        ...userData
      } : u);
      saveUsers(updatedUsers);
      showToast('success', 'Usuario actualizado', `${userData.name} ha sido actualizado`);
    } else {
      // Create new user
      const newUser: AdminUser = {
        ...userData,
        id: `user-${Date.now()}`,
        createdAt: new Date().toISOString()
      };
      saveUsers([...users, newUser]);
      showToast('success', 'Usuario creado', `${userData.name} ha sido creado`);
    }
    setShowModal(false);
    setEditingUser(null);
  };
  const filteredUsers = users.filter(u => u.name.toLowerCase().includes(searchTerm.toLowerCase()) || u.username.toLowerCase().includes(searchTerm.toLowerCase()) || u.email.toLowerCase().includes(searchTerm.toLowerCase()));
  return <div data-id="element-2589">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-2590">
        <div data-id="element-2591">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-2592">
            Usuarios Administrativos
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1" data-id="element-2593">
            Gestiona los usuarios con acceso al panel de administración
          </p>
        </div>
        <Button leftIcon={<Plus size={18} data-id="element-2595" />} onClick={handleAddUser} data-id="element-2594">
          Nuevo Usuario
        </Button>
      </div>

      <div className="mb-6" data-id="element-2596">
        <SearchBar placeholder="Buscar usuario..." onSearch={setSearchTerm} className="w-full sm:w-96" data-id="element-2597" />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-2598">
        <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800" data-id="element-2599">
          <div className="flex items-center gap-3" data-id="element-2600">
            <div className="p-3 bg-blue-100 dark:bg-blue-800 rounded-lg" data-id="element-2601">
              <User className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-2602" />
            </div>
            <div data-id="element-2603">
              <p className="text-xs text-blue-600 dark:text-blue-400 font-medium" data-id="element-2604">
                Total Usuarios
              </p>
              <p className="text-2xl font-bold text-blue-900 dark:text-blue-100" data-id="element-2605">
                {users.length}
              </p>
            </div>
          </div>
        </Card>

        <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800" data-id="element-2606">
          <div className="flex items-center gap-3" data-id="element-2607">
            <div className="p-3 bg-green-100 dark:bg-green-800 rounded-lg" data-id="element-2608">
              <Shield className="w-6 h-6 text-green-600 dark:text-green-400" data-id="element-2609" />
            </div>
            <div data-id="element-2610">
              <p className="text-xs text-green-600 dark:text-green-400 font-medium" data-id="element-2611">
                Activos
              </p>
              <p className="text-2xl font-bold text-green-900 dark:text-green-100" data-id="element-2612">
                {users.filter(u => u.status === 'Active').length}
              </p>
            </div>
          </div>
        </Card>

        <Card className="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800" data-id="element-2613">
          <div className="flex items-center gap-3" data-id="element-2614">
            <div className="p-3 bg-purple-100 dark:bg-purple-800 rounded-lg" data-id="element-2615">
              <Shield className="w-6 h-6 text-purple-600 dark:text-purple-400" data-id="element-2616" />
            </div>
            <div data-id="element-2617">
              <p className="text-xs text-purple-600 dark:text-purple-400 font-medium" data-id="element-2618">
                Administradores
              </p>
              <p className="text-2xl font-bold text-purple-900 dark:text-purple-100" data-id="element-2619">
                {users.filter(u => u.role === 'admin').length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Users Table */}
      <Card noPadding className="overflow-hidden" data-id="element-2620">
        <div className="overflow-x-auto" data-id="element-2621">
          <table className="w-full text-left border-collapse min-w-[800px]" data-id="element-2622">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700" data-id="element-2623">
              <tr data-id="element-2624">
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-2625">
                  Usuario
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-2626">
                  Rol
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-2627">
                  Permisos
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-2628">
                  Estado
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase text-right" data-id="element-2629">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900" data-id="element-2630">
              {filteredUsers.map(user => {
              const permissionCount = Object.values(user.permissions).filter(Boolean).length;
              return <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-id="element-2631">
                    <td className="px-6 py-4" data-id="element-2632">
                      <div data-id="element-2633">
                        <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2634">
                          {user.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2635">
                          @{user.username} • {user.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-2636">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${user.role === 'admin' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'}`} data-id="element-2637">
                        {user.role === 'admin' ? 'Administrador' : 'Supervisor'}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-2638">
                      <span className="text-sm text-gray-600 dark:text-gray-300" data-id="element-2639">
                        {permissionCount} de 6 permisos
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-2640">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${user.status === 'Active' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`} data-id="element-2641">
                        {user.status === 'Active' ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-2642">
                      <div className="flex items-center justify-end gap-2" data-id="element-2643">
                        <button onClick={() => handleEdit(user)} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 p-1" title="Editar" data-id="element-2644">
                          <Edit2 size={16} data-id="element-2645" />
                        </button>
                        {user.username !== 'admin' && <button onClick={() => handleDelete(user)} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 p-1" title="Eliminar" data-id="element-2646">
                            <Trash2 size={16} data-id="element-2647" />
                          </button>}
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* User Form Modal */}
      {showModal && <UserFormModal user={editingUser} onClose={() => {
      setShowModal(false);
      setEditingUser(null);
    }} onSave={handleSave} data-id="element-2648" />}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-2649">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-2650">
            <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-2651">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-2652">
                Eliminar Usuario
              </h2>
            </div>
            <div className="p-6" data-id="element-2653">
              <p className="text-gray-700 dark:text-gray-300 mb-4" data-id="element-2654">
                ¿Estás seguro que deseas eliminar a{' '}
                <strong data-id="element-2655">{deleteConfirm.name}</strong>? Esta acción no se puede
                deshacer.
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex gap-3" data-id="element-2656">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} fullWidth data-id="element-2657">
                Cancelar
              </Button>
              <Button onClick={confirmDelete} fullWidth className="bg-red-600 hover:bg-red-700" data-id="element-2658">
                Eliminar Usuario
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}
// User Form Modal Component
function UserFormModal({
  user,
  onClose,
  onSave
}: any) {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: user?.username || '',
    password: user?.password || '',
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role || 'supervisor',
    status: user?.status || 'Active',
    permissions: user?.permissions || {
      canManageOrders: false,
      canManageMenu: false,
      canManageEmployees: false,
      canManageCompanies: false,
      canViewReports: false,
      canManageUsers: false
    }
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-2659">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-2xl border border-gray-200 dark:border-gray-800 max-h-[90vh] overflow-y-auto" data-id="element-2660">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800 sticky top-0 bg-white dark:bg-gray-900 z-10" data-id="element-2661">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-2662">
            {user ? 'Editar Usuario' : 'Nuevo Usuario Administrativo'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-2663">
            <X size={20} data-id="element-2664" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-id="element-2665">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2666">
            <div data-id="element-2667">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2668">
                Nombre Completo
              </label>
              <input type="text" required value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Juan Pérez" data-id="element-2669" />
            </div>

            <div data-id="element-2670">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2671">
                Correo Electrónico
              </label>
              <input type="email" required value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="juan@empresa.com" data-id="element-2672" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2673">
            <div data-id="element-2674">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2675">
                Nombre de Usuario
              </label>
              <input type="text" required value={formData.username} onChange={e => setFormData({
              ...formData,
              username: e.target.value
            })} disabled={user?.username === 'admin'} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50" placeholder="juanperez" data-id="element-2676" />
            </div>

            <div data-id="element-2677">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2678">
                Contraseña {user && '(dejar en blanco para mantener)'}
              </label>
              <div className="relative" data-id="element-2679">
                <input type={showPassword ? 'text' : 'password'} required={!user} value={formData.password} onChange={e => setFormData({
                ...formData,
                password: e.target.value
              })} className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="••••••••" data-id="element-2680" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-2681">
                  {showPassword ? <EyeOff size={18} data-id="element-2682" /> : <Eye size={18} data-id="element-2683" />}
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2684">
            <div data-id="element-2685">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2686">
                Rol
              </label>
              <select value={formData.role} onChange={e => setFormData({
              ...formData,
              role: e.target.value as any
            })} disabled={user?.username === 'admin'} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50" data-id="element-2687">
                <option value="admin" data-id="element-2688">Administrador</option>
                <option value="supervisor" data-id="element-2689">Supervisor</option>
              </select>
            </div>

            <div data-id="element-2690">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2691">
                Estado
              </label>
              <select value={formData.status} onChange={e => setFormData({
              ...formData,
              status: e.target.value as any
            })} disabled={user?.username === 'admin'} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50" data-id="element-2692">
                <option value="Active" data-id="element-2693">Activo</option>
                <option value="Inactive" data-id="element-2694">Inactivo</option>
              </select>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-800 pt-4" data-id="element-2695">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-2696">
              Permisos de Acceso
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-2697">
              <label className="flex items-center gap-2 cursor-pointer" data-id="element-2698">
                <input type="checkbox" checked={formData.permissions.canManageOrders} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  canManageOrders: e.target.checked
                }
              })} className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black" data-id="element-2699" />
                <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2700">
                  Gestionar Órdenes
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer" data-id="element-2701">
                <input type="checkbox" checked={formData.permissions.canManageMenu} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  canManageMenu: e.target.checked
                }
              })} className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black" data-id="element-2702" />
                <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2703">
                  Gestionar Menú
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer" data-id="element-2704">
                <input type="checkbox" checked={formData.permissions.canManageEmployees} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  canManageEmployees: e.target.checked
                }
              })} className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black" data-id="element-2705" />
                <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2706">
                  Gestionar Empleados
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer" data-id="element-2707">
                <input type="checkbox" checked={formData.permissions.canManageCompanies} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  canManageCompanies: e.target.checked
                }
              })} className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black" data-id="element-2708" />
                <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2709">
                  Gestionar Empresas
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer" data-id="element-2710">
                <input type="checkbox" checked={formData.permissions.canViewReports} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  canViewReports: e.target.checked
                }
              })} className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black" data-id="element-2711" />
                <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2712">
                  Ver Reportes
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer" data-id="element-2713">
                <input type="checkbox" checked={formData.permissions.canManageUsers} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  canManageUsers: e.target.checked
                }
              })} disabled={user?.username === 'admin'} className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black disabled:opacity-50" data-id="element-2714" />
                <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2715">
                  Gestionar Usuarios
                </span>
              </label>
            </div>
          </div>

          <div className="flex gap-3 pt-4" data-id="element-2716">
            <Button type="button" variant="outline" onClick={onClose} fullWidth data-id="element-2717">
              Cancelar
            </Button>
            <Button type="submit" fullWidth data-id="element-2718">
              {user ? 'Actualizar Usuario' : 'Crear Usuario'}
            </Button>
          </div>
        </form>
      </div>
    </div>;
}